/* ==========================================================================
   SNUG REBUILD — PDP AJAX CART + SLIDE-OUT DRAWER  (assets/rebuild-cart.js)
   Owner: A5-pdp / JS-externalize fix (2026-06-14).

   WHY EXTERNAL: the store's lazyload optimizer rewrites every INLINE <script>
   to type=text/lazyload and never re-executes it. The old inline copy of this
   logic (in product-template.liquid) was therefore DEAD — the native form POST
   won and add-to-cart did a full-page reload instead of opening the drawer.
   External <script src> files load + run normally, so this lives here and is
   loaded with `defer` from sections/product-template.liquid.

   SELF-CONTAINED DRAWER: the slide-out drawer markup (#PremiumCartDrawer_Custom
   / #PremiumCartDrawerOverlay_Custom) is rendered globally by sections/
   cart-drawer.liquid, but its OWN open/fetch logic is ALSO inline (=lazyloaded
   =dead). So this file does NOT rely on the drawer's inline JS: it fetches the
   fresh drawer section HTML itself (`/cart?section_id=cart-drawer`), swaps the
   innerHTML, and shows the drawer with no full reload. Open/close/overlay/qty
   handlers are bound here too so the drawer is fully alive.

   TRACKING — MANDATORY, PRESERVED VERBATIM (Stape/Elevar/Klaviyo/Meta-CAPI):
   on every successful add this re-dispatches BOTH
     • CustomEvent('cart:updated',     { detail: <cart>, bubbles:true })
     • CustomEvent('ajaxProduct:added', { detail: <addResp>, bubbles:true })
   exactly as the legacy handler did. These two events are the bridge that lets
   the server-side GTM container see AJAX cart actions. DO NOT remove.
   ========================================================================== */
(function () {
  'use strict';

  // Resolve the product section root via the data attribute (replaces the old
  // inline `#shopify-section-{{ section.id }}` Liquid interpolation).
  var sectionEl = document.querySelector('[data-section-type="product"][data-section-id]')
               || document.querySelector('[data-section-type="product"]');
  var root = sectionEl
    ? (document.getElementById('shopify-section-' + sectionEl.getAttribute('data-section-id')) || sectionEl)
    : document;
  if (root.__rbCartBound) return;
  root.__rbCartBound = true;

  var CART_SECTION_ID = 'cart-drawer'; // section file name used for /cart?section_id=

  /* ====================== DRAWER (self-contained) ====================== */
  var drawer, overlay;
  function drawerEls() {
    drawer  = document.getElementById('PremiumCartDrawer_Custom');
    overlay = document.getElementById('PremiumCartDrawerOverlay_Custom');
    return drawer && overlay;
  }
  function drawerVisible() {
    var d = document.getElementById('PremiumCartDrawer_Custom');
    return !!(d && !d.hidden && d.getAttribute('aria-hidden') === 'false');
  }
  function openDrawer() {
    if (!drawerEls()) return;
    drawer.hidden = false; overlay.hidden = false;
    drawer.setAttribute('aria-hidden', 'false'); overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cd-no-scroll');
    var closeBtn = drawer.querySelector('.cd-close');
    if (closeBtn) { try { closeBtn.focus(); } catch (e) {} }
  }
  function closeDrawer() {
    if (!drawerEls()) return;
    drawer.hidden = true; overlay.hidden = true;
    drawer.setAttribute('aria-hidden', 'true'); overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cd-no-scroll');
  }

  // Fetch fresh drawer section HTML, swap innerHTML, then open. No full reload.
  var _fetchingDrawer = false;
  function fetchDrawerAndOpen() {
    if (!drawerEls()) { return Promise.resolve(false); }
    if (_fetchingDrawer) return Promise.resolve(true);
    _fetchingDrawer = true;
    drawer.classList.add('is-loading');
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    return fetch(rootUrl + 'cart?section_id=' + CART_SECTION_ID, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var temp = document.createElement('div'); temp.innerHTML = html;
        var nd = temp.querySelector('#PremiumCartDrawer_Custom');
        var no = temp.querySelector('#PremiumCartDrawerOverlay_Custom');
        if (nd && drawer) drawer.innerHTML = nd.innerHTML;
        if (no && overlay) overlay.innerHTML = no.innerHTML;
        drawer.classList.remove('is-loading');
        openDrawer();
        _fetchingDrawer = false;
        return true;
      })
      .catch(function () {
        if (drawer) drawer.classList.remove('is-loading');
        _fetchingDrawer = false;
        // last resort so customer always reaches their cart
        return false;
      });
  }

  // Close interactions (overlay click, .cd-close, Escape) — drawer's own inline
  // handlers are lazyloaded/dead, so bind here.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('#PremiumCartDrawerOverlay_Custom') || e.target.closest('.cd-close')) {
      closeDrawer();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerVisible()) closeDrawer();
  });
  // Cart-link clicks open the slide-out drawer (instead of navigating to /cart).
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (!/(^|\/)cart\/?($|\?|#)/.test(href)) return;
    if (a.hasAttribute('data-no-drawer')) return;
    e.preventDefault(); e.stopPropagation();
    fetchDrawerAndOpen().then(function (ok) { if (!ok) window.location.href = '/cart'; });
  }, true);

  /* ====================== HEADER COUNT HELPERS ====================== */
  function updateHeaderCount(count) {
    var n = String(count || 0);
    var selectors = [
      '[data-cart-count]', '[data-cart-count-bubble]', '#CartCount', '#CartCount span',
      '.cart-count-bubble', '.cart-count-bubble span', '.header__cart-count',
      '.header__cart-count span', '.site-header__cart-count', '.js-cart-count',
      'span.cart-count', '.cart-link__count', '.cart-link__bubble-num'
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.children.length === 0) el.textContent = n;
      });
    });
    var bubbles = document.querySelectorAll('#CartCount, [data-cart-count-bubble], .cart-count-bubble, .header__cart-count, .cart-link__bubble');
    bubbles.forEach(function (b) {
      if (count === 0) { b.classList.add('hide', 'cart-count-bubble--empty'); b.setAttribute('aria-hidden', 'true'); }
      else { b.classList.remove('hide', 'cart-count-bubble--empty'); b.removeAttribute('aria-hidden'); }
    });
  }
  function readCurrentHeaderCount() {
    var sel = '[data-cart-count], #CartCount, .cart-count-bubble, .header__cart-count, .cart-link__bubble-num, .cart-link__count, .js-cart-count, .site-header__cart-count';
    var els = document.querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) {
      if (els[i].children.length === 0) {
        var n = parseInt(els[i].textContent.trim(), 10);
        if (!isNaN(n)) return n;
      }
    }
    try { var c = parseInt(localStorage.getItem('snug_cart_count') || '0', 10); if (!isNaN(c)) return c; } catch (e) {}
    return 0;
  }
  function persistCartCount(n) { try { localStorage.setItem('snug_cart_count', String(n)); } catch (e) {} }
  function fetchAndUpdateHeaderCount() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) { if (c) { updateHeaderCount(c.item_count); persistCartCount(c.item_count); } return c; })
      .catch(function () { return null; });
  }

  /* ====================== TOAST ====================== */
  function showToast(msg, isError) {
    var t = document.getElementById('snug-pdp-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'snug-pdp-toast';
      t.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:160px;z-index:10000;padding:11px 18px;border-radius:9999px;font-size:13px;font-weight:700;box-shadow:0 6px 24px rgba(0,0,0,0.18);pointer-events:none;opacity:0;transition:opacity .25s ease,transform .25s ease;max-width:88vw;text-align:center;line-height:1.3;';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.background = isError ? '#fef2f2' : '#E3DED4';
    t.style.color = isError ? '#991b1b' : '#232323';
    t.style.border = '1px solid ' + (isError ? '#fecaca' : '#A89A86');
    requestAnimationFrame(function () { t.style.opacity = '1'; t.style.transform = 'translateX(-50%) translateY(0)'; });
    clearTimeout(t._hideT);
    t._hideT = setTimeout(function () { t.style.opacity = '0'; t.style.transform = 'translateX(-50%) translateY(8px)'; }, isError ? 3500 : 2000);
  }

  /* ====================== STICKY BUTTON STATE ====================== */
  var stickyBar = root.querySelector('.mobile-sticky-wrapper');
  var _stickyAtcBtn = stickyBar ? stickyBar.querySelector('[data-sticky-atc]') : null;
  var _buyNowBtn    = stickyBar ? stickyBar.querySelector('[data-buy-now]') : null;
  var _ajaxInFlight = false;
  function setStickyLoading(on) {
    if (!_stickyAtcBtn) return;
    _stickyAtcBtn.classList.toggle('is-loading', !!on);
    if (on) { _stickyAtcBtn.setAttribute('aria-busy', 'true'); _stickyAtcBtn.disabled = true; }
    else { _stickyAtcBtn.removeAttribute('aria-busy'); _stickyAtcBtn.disabled = false; }
  }
  function setBuyNowLoading(on) {
    if (!_buyNowBtn) return;
    _buyNowBtn.classList.toggle('is-loading', !!on);
    if (on) { _buyNowBtn.setAttribute('aria-busy', 'true'); _buyNowBtn.disabled = true; }
    else { _buyNowBtn.removeAttribute('aria-busy'); _buyNowBtn.disabled = false; }
  }

  // Sticky size-pill <-> main radio sync
  function syncStickyToVariant(variantId) {
    if (!stickyBar) return;
    stickyBar.querySelectorAll('.mobile-sticky-size').forEach(function (b) {
      var match = b.getAttribute('data-sticky-variant-id') === String(variantId);
      b.classList.toggle('is-selected', match);
      b.setAttribute('aria-checked', match ? 'true' : 'false');
    });
  }
  if (stickyBar) {
    stickyBar.addEventListener('click', function (e) {
      var pill = e.target.closest('.mobile-sticky-size');
      if (!pill || pill.disabled) return;
      e.preventDefault();
      var vid = pill.getAttribute('data-sticky-variant-id');
      if (!vid) return;
      var radio = root.querySelector('input[name="id"][type="radio"][value="' + vid + '"]');
      if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      syncStickyToVariant(vid);
    });
  }
  root.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'id') syncStickyToVariant(e.target.value);
  });

  /* ====================== AJAX ADD-TO-CART ====================== */
  function ajaxAddFromForm(form) {
    if (_ajaxInFlight) return;
    _ajaxInFlight = true;
    setStickyLoading(true);
    var fd = new FormData(form);
    var addQty = parseInt(fd.get('quantity') || '1', 10) || 1;
    var preCount = readCurrentHeaderCount();
    var optimisticCount = preCount + addQty;

    // optimistic header + cart:updated (preserved)
    updateHeaderCount(optimisticCount);
    persistCartCount(optimisticCount);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: optimisticCount, _optimistic: true }, bubbles: true }));

    fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return null; }).then(function (eb) {
            updateHeaderCount(preCount); persistCartCount(preCount);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: preCount }, bubbles: true }));
            throw new Error((eb && (eb.description || eb.message)) || 'Sepete eklenemedi. Lütfen tekrar deneyin.');
          });
        }
        return res.json();
      })
      .then(function (data) {
        // ===== TRACKING-CRITICAL: re-dispatch both events (Stape/Klaviyo/Meta) =====
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: data, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'rebuild-cart' }, bubbles: true }));
        // open the slide-out drawer (no full reload)
        fetchDrawerAndOpen();
        // reconcile authoritative count + final cart:updated with real cart
        fetchAndUpdateHeaderCount().then(function (cart) {
          if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
        });
      })
      .catch(function (err) {
        console.error('ATC error:', err);
        showToast(err && err.message ? err.message : 'Sepete eklenemedi.', true);
      })
      .then(function () { setStickyLoading(false); _ajaxInFlight = false; });
  }

  // Submit handler (capture) — catches form.submit() + native submit
  root.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.querySelector || !form.querySelector('.product-form__cart-submit')) return;
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    ajaxAddFromForm(form);
    return false;
  }, true);
  // Explicit click handler — some theme scripts intercept submit first
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('.product-form__cart-submit');
    if (!btn || btn.disabled) return;
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    var form = btn.closest('form');
    if (form) ajaxAddFromForm(form);
  }, true);

  /* ====================== cart:updated header sync ====================== */
  function extractItemCount(detail) {
    if (!detail) return null;
    if (typeof detail.item_count === 'number') return detail.item_count;
    if (detail.cart && typeof detail.cart.item_count === 'number') return detail.cart.item_count;
    return null;
  }
  document.addEventListener('cart:updated', function (e) {
    var n = extractItemCount(e.detail);
    if (typeof n === 'number') { updateHeaderCount(n); persistCartCount(n); }
    else fetchAndUpdateHeaderCount();
  });
  document.addEventListener('cart:refresh', function () { fetchAndUpdateHeaderCount(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') fetchAndUpdateHeaderCount();
  });

  /* ====================== BUY NOW (Hemen satın al) ====================== */
  // Empty cart -> add this variant + /checkout (with bfcache rollback flag).
  // Non-empty cart -> skip add, go straight to /checkout.
  var BUY_NOW_KEY = 'snug_buy_now_pending';
  function markBuyNowPending(variantId, key, qtyBefore) {
    try {
      sessionStorage.setItem(BUY_NOW_KEY, JSON.stringify({
        variant_id: String(variantId || ''), key: String(key || ''),
        qty_before: typeof qtyBefore === 'number' ? qtyBefore : 0,
        ts: Date.now(), return_url: window.location.href
      }));
    } catch (e) {}
  }
  function getCurrentQty(variantId) {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = (cart.items || []).find(function (it) { return String(it.variant_id) === String(variantId); });
        return line ? (line.quantity | 0) : 0;
      }).catch(function () { return 0; });
  }
  function rollbackBuyNowIfNeeded() {
    var raw; try { raw = sessionStorage.getItem(BUY_NOW_KEY); } catch (e) { return; }
    if (!raw) return;
    var pending; try { pending = JSON.parse(raw); } catch (e) { try { sessionStorage.removeItem(BUY_NOW_KEY); } catch (_) {} return; }
    if (!pending.ts || Date.now() - pending.ts > 20 * 60 * 1000) { try { sessionStorage.removeItem(BUY_NOW_KEY); } catch (_) {} return; }
    if (/\/(checkouts?|thank-you|orders\/)/i.test(window.location.pathname)) return;
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var found = (cart.items || []).find(function (it) {
          return String(it.variant_id) === pending.variant_id || (pending.key && String(it.key) === pending.key);
        });
        if (!found) { try { sessionStorage.removeItem(BUY_NOW_KEY); } catch (_) {} return; }
        var qtyBefore = (pending.qty_before | 0);
        if (found.quantity <= qtyBefore) { try { sessionStorage.removeItem(BUY_NOW_KEY); } catch (_) {} return; }
        return fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: found.key, quantity: qtyBefore }), credentials: 'same-origin'
        }).then(function (rm) {
          if (rm.ok) return rm.json().then(function (newCart) {
            updateHeaderCount(newCart.item_count);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: newCart, bubbles: true }));
          });
        });
      })
      .catch(function () {})
      .then(function () { try { sessionStorage.removeItem(BUY_NOW_KEY); } catch (_) {} });
  }
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-buy-now]');
    if (!btn || btn.disabled) return;
    e.preventDefault(); e.stopPropagation();
    if (_ajaxInFlight) return;
    _ajaxInFlight = true;
    setBuyNowLoading(true);
    var form = btn.closest('form');
    if (!form) { _ajaxInFlight = false; setBuyNowLoading(false); window.location.href = '/cart'; return; }
    var fd = new FormData(form);
    var variantId = fd.get('id');
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (currentCart) {
        if (currentCart && (currentCart.item_count | 0) > 0) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'buy-now-direct' }, bubbles: true }));
          setTimeout(function () { window.location.href = '/checkout'; }, 50);
          return;
        }
        return getCurrentQty(variantId).then(function (qtyBefore) {
          return fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
            .then(function (r) { if (!r.ok) return r.json().catch(function () { return null; }).then(function (eb) { throw new Error((eb && (eb.description || eb.message)) || 'Eklenemedi. Lütfen tekrar deneyin.'); }); return r.json(); })
            .then(function (data) {
              markBuyNowPending(variantId, data && data.key, qtyBefore);
              return fetchAndUpdateHeaderCount().then(function (cart) {
                var detail = cart || data;
                document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail, bubbles: true }));
                document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: detail, bubbles: true }));
                setTimeout(function () { window.location.href = '/checkout'; }, 50);
              });
            });
        });
      })
      .catch(function (err) {
        console.error('Buy Now error:', err);
        showToast(err && err.message ? err.message : 'Eklenemedi.', true);
        setBuyNowLoading(false); _ajaxInFlight = false;
        try { sessionStorage.removeItem(BUY_NOW_KEY); } catch (_) {}
      });
  }, true);

  window.addEventListener('pageshow', function () {
    _ajaxInFlight = false; setStickyLoading(false); setBuyNowLoading(false);
    rollbackBuyNowIfNeeded();
    fetchAndUpdateHeaderCount();
  });

  // expose for QA / verification
  window.__rbCartReady = true;
})();
